
import { useEffect, useRef, useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ReadyToStartSection from '../../../components/feature/ReadyToStartSection';

export default function Curriculum() {
  const [visibleSections, setVisibleSections] = useState<number[]>([]);
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  const curriculumAreas = [
    {
      title: 'Language & Literacy',
      description: 'Storytime, early writing, and vocabulary-building exercises.',
      icon: 'ri-book-open-line',
      color: '#A8D8F0'
    },
    {
      title: 'Math & Science',
      description: 'Hands-on experiments, sorting, counting, and exploring the natural world.',
      icon: 'ri-flask-line',
      color: '#BFE6CC'
    },
    {
      title: 'Creative Arts',
      description: 'Music, movement, drawing, and imaginative play that spark creativity.',
      icon: 'ri-palette-line',
      color: '#A8D8F0'
    },
    {
      title: 'Social & Emotional Learning',
      description: 'Group activities and discussions that build confidence, empathy, and independence.',
      icon: 'ri-heart-3-line',
      color: '#BFE6CC'
    },
    {
      title: 'Physical Development',
      description: 'Indoor and outdoor play to support gross and fine motor skills.',
      icon: 'ri-run-line',
      color: '#BFE6CC'
    }
  ];

  useEffect(() => {
    const observers = sectionsRef.current.map((section, index) => {
      if (!section) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => [...prev, index]);
          }
        },
        { threshold: 0.25 }
      );
      
      observer.observe(section);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7E8]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#2B2B2B] mb-6 text-center" style={{ fontFamily: '"Fredoka", sans-serif' }}>
            Our Child Care Curriculum
          </h2>
          <div className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <p 
                className="text-lg text-[#2B2B2B]/80 leading-relaxed text-justify"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                Our child care curriculum supports the whole child through a play-based, developmentally appropriate approach that fosters learning in all key areas. Daily activities include language and literacy, math and science, creative arts, social-emotional learning, and physical development. We create a safe, nurturing environment that encourages exploration, builds confidence, and prepares children for future learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Areas */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Philosophy Section */}
          <div className="mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#2E5939] mb-8 text-center"
              style={{ fontFamily: '"Poppins", sans-serif' }}
            >
              Our Educational Philosophy
            </h2>
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <p 
                className="text-lg text-[#2B2B2B]/80 leading-relaxed mb-6"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                At Creative Journeys Learning Center, we believe that children learn best through hands-on exploration, 
                creative expression, and meaningful interactions with their environment and peers. Our curriculum is 
                designed to nurture the whole child - intellectually, socially, emotionally, and physically.
              </p>
              <p 
                className="text-lg text-[#2B2B2B]/80 leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                We follow a play-based learning approach that encourages curiosity, critical thinking, and problem-solving 
                skills while building a strong foundation for future academic success.
              </p>
            </div>
          </div>

          {/* Daily Activities Heading */}
          <h3 
            className="text-2xl font-semibold text-[#2B2B2B] mb-12 text-center"
            style={{ fontFamily: '"Fredoka", sans-serif' }}
          >
            Our daily activities include:
          </h3>

          {/* Curriculum Areas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {curriculumAreas.map((area, index) => (
              <div
                key={index}
                ref={el => sectionsRef.current[index] = el}
                className={`p-6 rounded-xl bg-white shadow-lg border border-gray-100 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
                  visibleSections.includes(index) 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-5 opacity-0'
                }`}
              >
                <div 
                  className="w-16 h-16 flex items-center justify-center rounded-full mb-4 transition-transform duration-150 hover:scale-110"
                  style={{ backgroundColor: `${area.color}20` }}
                >
                  <i className={`${area.icon} text-2xl`} style={{ color: area.color }}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#333333] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {area.title}
                </h3>
                <p 
                  className="text-[#333333]/80 leading-relaxed"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
